/**
 * HTTP Formatting Utilities
 *
 * This module re-exports the HTTP parsing/formatting functions from the workers module.
 * The worker implementations are used to offload CPU-intensive parsing to Web Workers
 * for large content, while automatically falling back to sync for small content.
 *
 * For async worker-based parsing (recommended for large bodies), use useHttpParser hook:
 * ```
 * import { useHttpParser } from '../workers';
 * const { formatRequest, parseRequest } = useHttpParser();
 * const formatted = await formatRequest({ method, url, headers, body });
 * ```
 */

// Re-export sync functions from workers module for backward compatibility
// These are the same implementations but centralized in the workers module
export {
  formatRequestToText,
  parseRequestFromText,
  formatResponseToText,
  parseResponseFromText,
} from '../workers';

// Also export the hook for async usage with large content
export { useHttpParser } from '../workers';
export { getDefaultStatusMessage } from './http-status';

export type ConnectionTarget = {
  hostname: string;
  port: number;
  https: boolean;
};

export function extractConnectionTarget(url: string): ConnectionTarget {
  try {
    const parsed = new URL(url);

    return {
      hostname: parsed.hostname,
      port: parseInt(parsed.port) || (parsed.protocol === 'https:' ? 443 : 80),
      https: parsed.protocol === 'https:',
    };
  } catch {
    const stripped = url.replace(/^\/+/, '');

    if (stripped && !stripped.startsWith('/')) {
      try {
        const withScheme = new URL(`https://${stripped}`);

        if (
          withScheme.hostname &&
          withScheme.hostname !== 'localhost' &&
          withScheme.hostname.includes('.')
        ) {
          return {
            hostname: withScheme.hostname,
            port:
              parseInt(withScheme.port) ||
              (withScheme.protocol === 'https:' ? 443 : 80),
            https: true,
          };
        }
      } catch {
        // fall through
      }
    }

    return { hostname: 'localhost', port: 443, https: true };
  }
}

export function formatConnectionTarget(target: ConnectionTarget): string {
  const protocol = target.https ? 'https' : 'http';
  const defaultPort = target.https ? 443 : 80;
  const portStr = target.port === defaultPort ? '' : `:${target.port}`;

  return `${protocol}://${target.hostname}${portStr}`;
}

/** Maximum body size for sending to chat (100KB) */
export const MAX_BODY_FOR_CHAT = 100 * 1024;

/**
 * Truncates a body if it exceeds the maximum size for chat/comparer.
 * Returns the original body if under the limit, or a truncated version with a message.
 */
export function truncateBodyForChat(
  body: string | undefined,
): string | undefined {
  if (!body || body.length <= MAX_BODY_FOR_CHAT) {
    return body;
  }

  const truncatedKB = ((body.length - MAX_BODY_FOR_CHAT) / 1024).toFixed(0);

  return (
    body.slice(0, MAX_BODY_FOR_CHAT) +
    `\n\n[Truncated: ${truncatedKB}KB omitted - body too large for chat]`
  );
}

/**
 * Checks if a body is large and would be truncated.
 * Returns true if the body exceeds MAX_BODY_FOR_CHAT.
 */
export function isBodyTooLargeForChat(body: string | undefined): boolean {
  return !!body && body.length > MAX_BODY_FOR_CHAT;
}

export function stringifyMaybeJsonBody(
  body: Record<string, unknown> | string | undefined,
): string | undefined {
  if (body === undefined) {
    return undefined;
  }

  return typeof body === 'string' ? body : JSON.stringify(body, null, 2);
}
